import { generateNodeIndexing } from "../../../hooks/useNodeIndexing";
import { extractVersionStructure } from "../../../util/getAuditTrailData";

// Helper constants and functions for node indexing
const CHILD_NODE_ID_FIELDS = [
  'attachment_tree_node_id',
  'video_tree_node_id',
  'rich_text_node_id',
  'canvas_tree_node_id',
  'form_tree_node_id',
];

const getSiblingOrderNumber = (node) => {
  if (!node) return 0;
  const order =
    node.sibling_order ??
    node.procedure_tree?.sibling_order ??
    node.detail?.procedure_tree?.sibling_order ??
    '0';
  const parsed = parseInt(order, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortBySiblingOrder = (items = []) =>
  [...items].sort((a, b) => getSiblingOrderNumber(a) - getSiblingOrderNumber(b));

const getChildNodeId = (child) => {
  if (!child) return null;
  if (child.tree_node_id) return child.tree_node_id;
  if (child.node_id) return child.node_id;
  if (child.sub_step_tree_node_id) return child.sub_step_tree_node_id;
  const detail = child.detail || {};
  for (const field of CHILD_NODE_ID_FIELDS) {
    if (detail[field]) {
      return detail[field];
    }
  }
  return null;
};

const resolveNodeNameWithFallback = (indexMap, id, node, defaultLabel) => {
  if (id && indexMap && indexMap[id]) {
    return indexMap[id];
  }
  if (node) {
    const candidateFields = [
      'title',
      'name',
      'label',
      'display_name',
      'section_name',
      'step_name',
      'substep_name',
    ];
    for (const field of candidateFields) {
      if (node[field]) {
        return node[field];
      }
    }
    if (node.detail) {
      for (const field of candidateFields) {
        if (node.detail[field]) {
          return node.detail[field];
        }
      }
    }
    if (node.type) {
      const type = node.type.toString();
      return type.charAt(0).toUpperCase() + type.slice(1);
    }
  }
  return defaultLabel;
};

const collectHierarchyNodesForNewRevision = (sections = [], indexMap = {}) => {
  const nodes = [];

  const addNode = (entry) => {
    const { id } = entry;
    if (!id) return;
    if (nodes.some((existing) => existing.id === id)) return;
    nodes.push(entry);
  };

  const sortedSections = sortBySiblingOrder(sections);
  sortedSections.forEach((section) => {
    const sectionId = section?.tree_node_id || section?.section_id_ref;
    const sectionLabel = resolveNodeNameWithFallback(indexMap, sectionId, section, 'Section');
    addNode({ id: sectionId, label: sectionLabel, type: 'section' });

    const sectionChildren = sortBySiblingOrder(section?.children || []);
    sectionChildren.forEach((child) => {
      const childId = getChildNodeId(child);
      const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
      addNode({
        id: childId,
        label: childLabel,
        type: 'sectionChild',
        parentSectionLabel: sectionLabel,
      });
    });

    const sortedSteps = sortBySiblingOrder(section?.steps || []);
    sortedSteps.forEach((step) => {
      const stepId = step?.tree_node_id || step?.step_id_ref;
      const stepLabel = resolveNodeNameWithFallback(indexMap, stepId, step, 'Step');
      addNode({
        id: stepId,
        label: stepLabel,
        type: 'step',
        parentSectionLabel: sectionLabel,
      });

      const stepChildren = sortBySiblingOrder(step?.children || []);
      stepChildren.forEach((child) => {
        const childId = getChildNodeId(child);
        const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
        addNode({
          id: childId,
          label: childLabel,
          type: 'stepChild',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });
      });

      const sortedSubsteps = sortBySiblingOrder(step?.substeps || []);
      sortedSubsteps.forEach((substep) => {
        const substepId = substep?.tree_node_id || substep?.substep_id_ref;
        const substepLabel = resolveNodeNameWithFallback(indexMap, substepId, substep, 'Substep');
        addNode({
          id: substepId,
          label: substepLabel,
          type: 'substep',
          parentSectionLabel: sectionLabel,
          parentStepLabel: stepLabel,
        });

        const substepChildren = sortBySiblingOrder(substep?.children || []);
        substepChildren.forEach((child) => {
          const childId = getChildNodeId(child);
          const childLabel = resolveNodeNameWithFallback(indexMap, childId, child, 'Element');
          addNode({
            id: childId,
            label: childLabel,
            type: 'substepChild',
            parentSectionLabel: sectionLabel,
            parentStepLabel: stepLabel,
            parentSubstepLabel: substepLabel,
          });
        });
      });
    });
  });

  return nodes;
};

const findNodeInSource = (sourceNodes, nodeId, type) => {
  for (const section of sourceNodes) {
    if ((section.tree_node_id === nodeId || section.section_id_ref === nodeId) && type === 'section') {
      return section;
    }
    
    if (section.children) {
      for (const child of section.children) {
        const childId = getChildNodeId(child);
        if (childId === nodeId && type === 'sectionChild') {
          return child;
        }
      }
    }
    
    if (section.steps) {
      for (const step of section.steps) {
        if ((step.tree_node_id === nodeId || step.step_id_ref === nodeId) && type === 'step') {
          return step;
        }
        
        if (step.children) {
          for (const child of step.children) {
            const childId = getChildNodeId(child);
            if (childId === nodeId && type === 'stepChild') {
              return child;
            }
          }
        }
        
        if (step.substeps) {
          for (const substep of step.substeps) {
            if ((substep.tree_node_id === nodeId || substep.substep_id_ref === nodeId) && type === 'substep') {
              return substep;
            }
            
            if (substep.children) {
              for (const child of substep.children) {
                const childId = getChildNodeId(child);
                if (childId === nodeId && type === 'substepChild') {
                  return child;
                }
              }
            }
          }
        }
      }
    }
  }
  return null;
};

const stripUnaddedChildren = (node, type) => {
  const stripped = JSON.parse(JSON.stringify(node));
  
  if (type === 'section') {
    delete stripped.steps;
    delete stripped.children;
  }
  
  if (type === 'step') {
    delete stripped.substeps;
    delete stripped.children;
  }
  
  if (type === 'substep') {
    delete stripped.children;
  }
  
  return stripped;
};

const addNodeToStructure = (currentStructure, hierarchyNode, sourceNodes) => {
  const clonedStructure = JSON.parse(JSON.stringify(currentStructure));
  const { type, id } = hierarchyNode;
  
  const nodeData = findNodeInSource(sourceNodes, id, type);
  if (!nodeData) return clonedStructure;
  
  const clonedNode = stripUnaddedChildren(nodeData, type);
  
  if (type === 'section') {
    clonedStructure.push(clonedNode);
    clonedStructure.forEach((section, index) => {
      section.sibling_order = index.toString();
    });
  } else if (type === 'sectionChild') {
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.children) parentSection.children = [];
      parentSection.children.push(clonedNode);
      parentSection.children.forEach((child, index) => {
        child.sibling_order = index.toString();
      });
    }
  } else if (type === 'step') {
    const parentSection = clonedStructure.find(sec => 
      sec.tree_node_id === hierarchyNode.parentSectionId || 
      sec.section_id_ref === hierarchyNode.parentSectionId
    );
    if (parentSection) {
      if (!parentSection.steps) parentSection.steps = [];
      parentSection.steps.push(clonedNode);
      parentSection.steps.forEach((step, index) => {
        step.sibling_order = index.toString();
      });
    }
  } else if (type === 'stepChild') {
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.children) parentStep.children = [];
          parentStep.children.push(clonedNode);
          parentStep.children.forEach((child, index) => {
            child.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substep') {
    for (const section of clonedStructure) {
      if (section.steps) {
        const parentStep = section.steps.find(st => 
          st.tree_node_id === hierarchyNode.parentStepId || 
          st.step_id_ref === hierarchyNode.parentStepId
        );
        if (parentStep) {
          if (!parentStep.substeps) parentStep.substeps = [];
          parentStep.substeps.push(clonedNode);
          parentStep.substeps.forEach((substep, index) => {
            substep.sibling_order = index.toString();
          });
          break;
        }
      }
    }
  } else if (type === 'substepChild') {
    for (const section of clonedStructure) {
      if (section.steps) {
        for (const step of section.steps) {
          if (step.substeps) {
            const parentSubstep = step.substeps.find(ss => 
              ss.tree_node_id === hierarchyNode.parentSubstepId || 
              ss.substep_id_ref === hierarchyNode.parentSubstepId
            );
            if (parentSubstep) {
              if (!parentSubstep.children) parentSubstep.children = [];
              parentSubstep.children.push(clonedNode);
              parentSubstep.children.forEach((child, index) => {
                child.sibling_order = index.toString();
              });
              break;
            }
          }
        }
      }
    }
  }
  
  return clonedStructure;
};

/**
 * Build node indexing for new revision creation
 * Similar to buildCopyReplaceNodeIndexing but for new revision (starts with empty structure)
 * 
 * @param {Object} params - Parameters object
 * @param {Array} params.templateNodes - Array of template nodes to index
 * @param {Object} params.sourceDocument - Source document info (partNumber, revision)
 * @param {Object} params.targetDocument - Target document info (partNumber, revision)
 * @returns {Object} Node indexing object with structure:
 * {
 *   [nodeId]: {
 *     title: string,
 *     originalContent: Array,
 *     newContent: Array
 *   }
 * }
 */
export const buildNewRevisionNodeIndexing = ({
  templateNodes = [],
  sourceDocument,
  targetDocument,
}) => {
  if (!Array.isArray(templateNodes) || templateNodes.length === 0) {
    return {};
  }

  const indexMap = generateNodeIndexing(templateNodes);
  const hierarchyNodes = collectHierarchyNodesForNewRevision(templateNodes, indexMap);
  if (hierarchyNodes.length === 0) {
    return {};
  }

  const actionPrefix = `User created new revision ${targetDocument?.partNumber || ''}, Rev ${targetDocument?.revision || ''} based on ${sourceDocument?.partNumber || ''}, Rev ${sourceDocument?.revision || ''}`;
  const totalOperations = hierarchyNodes.length;
  const nodeIndexing = {};

  let currentStructure = [];
  const baseTargetSnapshot = []; // New revision starts empty
  let previousSnapshot = null;

  hierarchyNodes.forEach((hierarchyNode, index) => {
    const { id, label, type, parentSectionLabel, parentStepLabel, parentSubstepLabel } = hierarchyNode;
    
    // Store parent IDs for finding parent nodes
    if (type === 'sectionChild' || type === 'step' || type === 'substep') {
      for (const section of templateNodes) {
        if (type === 'sectionChild' && section.children) {
          const child = section.children.find(c => getChildNodeId(c) === id);
          if (child) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'step' && section.steps) {
          const step = section.steps.find(s => (s.tree_node_id === id || s.step_id_ref === id));
          if (step) {
            hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
            break;
          }
        } else if (type === 'substep' && section.steps) {
          for (const step of section.steps) {
            if (step.substeps) {
              const substep = step.substeps.find(ss => (ss.tree_node_id === id || ss.substep_id_ref === id));
              if (substep) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                break;
              }
            }
          }
        }
      }
    }
    
    if (type === 'stepChild' || type === 'substepChild') {
      for (const section of templateNodes) {
        if (section.steps) {
          for (const step of section.steps) {
            if (type === 'stepChild' && step.children) {
              const child = step.children.find(c => getChildNodeId(c) === id);
              if (child) {
                hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                break;
              }
            } else if (type === 'substepChild' && step.substeps) {
              for (const substep of step.substeps) {
                if (substep.children) {
                  const child = substep.children.find(c => getChildNodeId(c) === id);
                  if (child) {
                    hierarchyNode.parentSectionId = section.tree_node_id || section.section_id_ref;
                    hierarchyNode.parentStepId = step.tree_node_id || step.step_id_ref;
                    hierarchyNode.parentSubstepId = substep.tree_node_id || substep.substep_id_ref;
                    break;
                  }
                }
              }
            }
          }
        }
      }
    }
    
    let destinationDescription = '';
    const targetDescriptor = `${targetDocument?.partNumber || ''}, Rev ${targetDocument?.revision || ''}`;

    switch (type) {
      case 'section':
        destinationDescription = `${targetDescriptor}`;
        break;
      case 'step':
        destinationDescription = `${parentSectionLabel || 'the target section'} in ${targetDescriptor}`;
        break;
      case 'sectionChild':
        destinationDescription = `${parentSectionLabel || 'the target section'} in ${targetDescriptor}`;
        break;
      case 'stepChild':
        destinationDescription = `${parentStepLabel || 'the target step'} in ${targetDescriptor}`;
        break;
      case 'substep':
        destinationDescription = `${parentStepLabel || 'the target step'} in ${targetDescriptor}`;
        break;
      case 'substepChild':
        destinationDescription = `${parentSubstepLabel || 'the target substep'} in ${targetDescriptor}`;
        break;
      default:
        destinationDescription = targetDescriptor;
        break;
    }

    const title = `${actionPrefix}. System Performs Operation ${index + 1} of ${totalOperations}, Copy ${label} into ${destinationDescription}.`;
    
    const originalContent = previousSnapshot
      ? JSON.parse(JSON.stringify(previousSnapshot))
      : JSON.parse(JSON.stringify(baseTargetSnapshot));
    
    // Add the node to structure
    currentStructure = addNodeToStructure(currentStructure, hierarchyNode, templateNodes);
    
    // Capture newContent (state after adding this node)
    const newContent = currentStructure || [];
    
    // Store in node_indexing with new structure
    nodeIndexing[id] = {
      title,
      originalContent: originalContent || [],
      newContent: JSON.parse(JSON.stringify(newContent)),
    };
    
    previousSnapshot = JSON.parse(JSON.stringify(newContent));
  });

  return nodeIndexing;
};

